// LegalMarkdown — a small, dependency-free GFM renderer tuned for the legal/company pages.
//
// Legal content is authored + stored as Markdown (editable by the platform admin, served from the DB),
// so it renders here with premium legal typography, an auto table-of-contents (from `##` sections), and
// the brand treatment: every "Vidyapeeth360" gets the logo wordmark (blue "Vidyapeeth" + orange "360")
// and bare e-mail addresses become mailto links. It reuses the proven parsing approach of
// components/ui/Markdown.js but with page-scale styling + anchors; it is XSS-safe (React text nodes only,
// no dangerouslySetInnerHTML; links restricted to http(s)/mailto/relative).
import React from "react";
import BrandWordmarkText from "../../components/BrandWordmarkText";

const SAFE_HREF = /^(https?:\/\/|mailto:|\/(?!\/))/i;
const LIST_ITEM = /^(\s*)([-*+]|\d+\.)\s+(.*)$/;
const EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Stable anchor id from a heading ("1. Definitions" -> "1-definitions").
export const slugify = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// The section headings (`## ...`) drive the table of contents.
export function extractHeadings(md) {
  return String(md || "").replace(/\r\n/g, "\n").split("\n")
    .map((l) => /^##\s+(.+?)\s*$/.exec(l.trim().startsWith("#") ? l.trim() : ""))
    .filter(Boolean)
    .map((m) => ({ id: slugify(m[1]), label: m[1] }));
}

// The Vidyapeeth360 wordmark, inline — mirrors the logo lockup (brand-blue "Vidyapeeth" + orange "360").
function Brand() {
  return <BrandWordmarkText className="font-semibold" />;
}

// Wrap plain-text runs: brand mentions + bare e-mail addresses.
const TOKEN = /(Vidyapeeth360|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
function renderText(text, kp) {
  return text.split(TOKEN).map((part, i) => {
    if (part === "Vidyapeeth360") return <Brand key={`${kp}b${i}`} />;
    if (EMAIL.test(part)) return <a key={`${kp}e${i}`} href={`mailto:${part}`} className="text-primary font-medium hover:underline">{part}</a>;
    return <React.Fragment key={`${kp}t${i}`}>{part}</React.Fragment>;
  });
}

// Inline: **bold**, *italic*/_italic_, `code`, [text](url) — with brand/email applied to plain runs.
function renderInline(text, kp) {
  if (!text) return null;
  const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  const out = [];
  let last = 0, m, i = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(...renderText(text.slice(last, m.index), `${kp}-${i}p`));
    const tok = m[0];
    const k = `${kp}-i${i++}`;
    if (tok.startsWith("**") || tok.startsWith("__")) out.push(<strong key={k} className="font-semibold text-ink">{renderText(tok.slice(2, -2), `${k}strong`)}</strong>);
    else if (tok.startsWith("`")) out.push(<code key={k} className="px-1 py-0.5 rounded bg-muted text-ink text-[0.9em] font-mono">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("[")) {
      const mm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      const href = mm ? mm[2] : "";
      if (mm && SAFE_HREF.test(href)) {
        const external = /^https?:\/\//i.test(href);
        out.push(
          <a key={k} href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}
            className="text-primary font-medium underline underline-offset-2 hover:no-underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            {renderText(mm[1], `${k}link`)}
          </a>
        );
      } else out.push(mm ? mm[1] : tok);
    } else out.push(<em key={k}>{renderText(tok.slice(1, -1), `${k}em`)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(...renderText(text.slice(last), `${kp}-${i}p`));
  return out;
}

const splitRow = (line) => line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
function getListItem(line) {
  const m = LIST_ITEM.exec(line);
  if (!m) return null;
  return { indent: m[1].replace(/\t/g, "  ").length, ordered: /\d+\./.test(m[2]), text: m[3] };
}

function parseList(lines, idx, indent, kp) {
  const ordered = getListItem(lines[idx]).ordered;
  const items = [];
  let i = idx;
  while (i < lines.length) {
    const item = getListItem(lines[i]);
    if (!item || item.indent < indent) break;
    if (item.indent > indent) {
      if (!items.length) break;
      const nested = parseList(lines, i, item.indent, `${kp}c`);
      items[items.length - 1].children.push(nested.node);
      i = nested.next;
      continue;
    }
    if (item.ordered !== ordered) break;
    items.push({ text: item.text, children: [] });
    i++;
  }
  const Tag = ordered ? "ol" : "ul";
  return {
    next: i,
    node: (
      <Tag key={kp} className={`${ordered ? "list-decimal pl-5" : "list-none"} space-y-2 my-1`}>
        {items.map((it, x) => (
          <li key={x} className={ordered ? "pl-1 marker:text-ink-subtle marker:font-medium" : "flex gap-2.5"}>
            {!ordered && <span className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-orange-text/70" aria-hidden="true" />}
            <span className="min-w-0">{renderInline(it.text, `${kp}-${x}`)}{it.children}</span>
          </li>
        ))}
      </Tag>
    ),
  };
}

function parseBlocks(content) {
  const lines = String(content || "").replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let i = 0;
  const key = () => `b${out.length}`;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      if (!h[2].trim()) { i++; continue; }
      const depth = h[1].length;
      if (depth <= 2) {
        out.push(<h2 key={key()} id={slugify(h[2])} className="scroll-mt-[128px] font-heading font-semibold text-xl text-ink pt-7 first:pt-0">{renderInline(h[2], key())}</h2>);
      } else {
        out.push(<h3 key={key()} className="font-heading font-semibold text-[15px] text-ink pt-4">{renderInline(h[2], key())}</h3>);
      }
      i++; continue;
    }

    if (/^(\s*[-*_]){3,}\s*$/.test(line)) { i++; continue; } // horizontal rule → section spacing already handled

    // GFM table
    if (line.includes("|") && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes("-")) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) { rows.push(splitRow(lines[i])); i++; }
      out.push(
        <div key={key()} tabIndex={0} className="my-1 overflow-x-auto rounded-xl border border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-muted"><tr>{header.map((c, x) => <th key={x} scope="col" className="border-b border-line px-4 py-2.5 text-left font-semibold text-ink">{renderInline(c, `${key()}h${x}`)}</th>)}</tr></thead>
            <tbody>{rows.map((r, y) => <tr key={y} className="border-b border-line last:border-0">{r.map((c, x) => <td key={x} className="px-4 py-2.5 align-top text-ink-2">{renderInline(c, `${key()}r${y}c${x}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>);
      continue;
    }

    // Blockquote → premium call-out note
    if (/^\s*>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^\s*>\s?/, "")); i++; }
      out.push(<div key={key()} className="my-1 rounded-xl border border-line border-l-4 border-l-orange bg-muted/60 px-4 py-3.5 text-[15px] leading-relaxed text-ink-2">{renderInline(buf.join(" "), key())}</div>);
      continue;
    }

    const li = getListItem(line);
    if (li) { const p = parseList(lines, i, li.indent, key()); out.push(p.node); i = p.next; continue; }

    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|\s*>\s?|\s*[-*+]\s+|\s*\d+\.\s+)/.test(lines[i]) && !/^(\s*[-*_]){3,}\s*$/.test(lines[i]) && !lines[i].includes("|")) {
      para.push(lines[i]); i++;
    }
    if (para.length) out.push(<p key={key()} className="text-[15px] leading-relaxed text-ink-2">{renderInline(para.join(" "), key())}</p>);
    else { i++; } // a stray table/pipe line without a header separator — skip rather than loop
  }
  return out;
}

export default function LegalMarkdown({ source, className }) {
  const blocks = React.useMemo(() => parseBlocks(source), [source]);
  return <div className={`space-y-4 ${className || ""}`}>{blocks}</div>;
}
